import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import ContentWrapper from '@vio/atlas/helpers/ContentWrapper'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {TrustpilotWidget} from '@vio/shared/src/search/footer/TrustpilotWidget'
import {getLanguageCode} from 'modules/meta/selectors'
import dynamic from 'next/dynamic'

import {Brand} from '@daedalus/core/src/brand/types'

import {FooterCopyrightSection} from './CopyrightSection/CopyrightSection'
import {FooterLinksSection} from './LinksSection/LinksSection'
import {FooterSocialSection} from './SocialSection/SocialSection'

const FooterGetTheAppSection = dynamic(
  () =>
    import('./GetTheAppSection/GetTheAppSection').then(mod => ({
      default: mod.FooterGetTheAppSection
    })),
  {ssr: false}
)

const Wrapper = styled.div`
  color: white;
  background-color: ${cssTheme.colors.background.special.c950};
`

const FooterCopyrightWrapper = styled(ContentWrapper)`
  color: white;
  border-top: 1px solid ${cssTheme.colors.background.special.c900};
  margin-top: ${cssTheme.layout.spacing.s800};
`

interface Props {
  brand: Brand
}

export const Footer = ({brand}: Props) => {
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const lang = useSelector(getLanguageCode)

  const isSingleColumn = isMobile || isDesktopXs
  return (
    <Wrapper>
      <Box
        paddingTop="s900"
        paddingBottom="s900"
        paddingLeft="s600"
        paddingRight="s600"
      >
        <StyledContentWrapper paddingBottom="s600">
          <Box display="flex" justifyContent="space-between">
            <LayoutContainer>
              <FooterLinksSection brand={brand} />
              {isSingleColumn && (
                <Box display="flex" justifyContent="center">
                  <TrustpilotWidget
                    lang={lang}
                    variant="horizontal"
                    theme="dark"
                  />
                </Box>
              )}
              <FooterSocialSection
                brand={brand}
                isSingleColumn={isSingleColumn}
              />
              <FooterGetTheAppSection isSingleColumn={isSingleColumn} />
            </LayoutContainer>
            {!isSingleColumn && (
              <TrustpilotWidget lang={lang} variant="mini" theme="dark" />
            )}
          </Box>
        </StyledContentWrapper>
        <FooterCopyrightWrapper paddingTop="s800">
          <FooterCopyrightSection brand={brand} />
        </FooterCopyrightWrapper>
      </Box>
    </Wrapper>
  )
}

const StyledContentWrapper = styled(ContentWrapper)`
  max-width: 1120px;
  margin: auto;
`

const LayoutContainer = styled.div`
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s700};
  ${linariaMq.desktopSm} {
    display: grid;
    column-gap: ${cssTheme.layout.spacing.s600};
    flex-basis: 70%;
    grid-template-columns: repeat(3, 1fr);
    text-align: left;
  }
`
